import { useState } from 'react';
import { FileText, BookOpen, TrendingUp, Brain } from 'lucide-react';
import { motion } from 'framer-motion';
import FormationGestionDocumentaireModal from './FormationGestionDocumentaireModal';

interface FormationGestionDocumentaireCardProps {
  department: string;
  domain: string;
  index?: number;
}

// Leaders pour la Formation Gestion Documentaire
const formationLeaders = [
  { name: 'Jean-Pierre Leclerc', title: 'Directeur TI', avatar: '/JP.png' },
  { name: 'Eric Hains', title: 'Directeur TI', avatar: '/EH.png' },
  { name: 'Vincent Bourassa', title: 'Directeur TI', avatar: '/VB.png' },
  { name: 'Jean-Michel Caouette', title: 'Directeur TI', avatar: '/JMC.png' },
];

export default function FormationGestionDocumentaireCard({
  department,
  domain,
  index = 0,
}: FormationGestionDocumentaireCardProps) {
  const [isModalOpen, setIsModalOpen] = useState(false);

  return (
    <>
      <motion.button
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: index * 0.05 }}
        whileHover={{ scale: 1.02, y: -5 }}
        onClick={() => setIsModalOpen(true)}
        className="relative isolate h-72 w-full overflow-hidden rounded-lg bg-indigo-900 shadow-md hover:shadow-xl transition-all duration-300 cursor-pointer group border-2 border-bz-blue/30 hover:border-bz-blue"
      >
        {/* Background gradient */}
        <div className="absolute inset-0 z-0 bg-gradient-to-br from-bz-blue via-indigo-600 to-purple-700 opacity-90 group-hover:opacity-100 transition-opacity duration-300" />
        
        {/* Pattern overlay */}
        <div className="absolute inset-0 z-0 opacity-10" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
        }} />

        {/* Badge AI - Disponible dans AI Assistant */}
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ delay: 0.3, type: "spring", stiffness: 200 }}
          className="absolute top-3 left-3 z-10 flex items-center gap-1.5 px-3 py-1.5 backdrop-blur-sm bg-purple-500/40 border border-purple-400/60 rounded-full shadow-lg shadow-purple-500/20"
          title="Disponible dans AI Assistant"
        >
          <Brain className="w-4 h-4 text-purple-200" />
          <span className="text-xs text-purple-100 font-semibold">AI</span>
        </motion.div>

        {/* Content */}
        <div className="relative z-[1] flex h-full flex-col justify-between p-6 text-white">
          <div>
            <div className="flex items-center gap-3 mb-3">
              <div className="w-12 h-12 rounded-xl bg-white/20 backdrop-blur-sm flex items-center justify-center shadow-inner group-hover:bg-white/30 transition-colors">
                <FileText className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="text-lg font-bold drop-shadow">Formation Gestion Documentaire</h3>
                <p className="text-xs text-white/80 drop-shadow">{department}</p>
              </div>
            </div>
            
            <p className="text-sm text-white/90 leading-relaxed drop-shadow mb-4">
              Découvrez comment créer <strong>300-400 livrables tangibles</strong> de valeur pour vos clients. 
              Apprenez la méthodologie complète de création de documents normés ITIL/COBIT.
            </p>
          </div>

          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-xs text-white/80">
              <BookOpen className="w-4 h-4" />
              <span>Formation holistique</span>
            </div>
            <div className="flex items-center gap-2">
              {/* Avatars des leaders */}
              <div className="flex -space-x-2">
                {formationLeaders.map((leader, idx) => (
                  <div
                    key={idx}
                    className="w-7 h-7 rounded-full border-2 border-white/50 bg-white/20 backdrop-blur-sm overflow-hidden hover:scale-110 transition-transform cursor-pointer"
                    title={`${leader.name} - ${leader.title}`}
                  >
                    <img
                      src={leader.avatar}
                      alt={leader.name}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        target.style.display = 'none';
                        if (target.parentElement) {
                          target.parentElement.innerHTML = `<div class="w-full h-full flex items-center justify-center text-white text-[10px] font-bold">${leader.name.split(' ').map(n => n[0]).join('')}</div>`;
                        }
                      }}
                    />
                  </div>
                ))}
              </div>
              <div className="flex items-center gap-2 text-xs font-semibold text-white">
                <span>En savoir plus</span>
                <TrendingUp className="w-4 h-4" />
              </div>
            </div>
          </div>
        </div>

        {/* Shine effect on hover */}
        <div className="absolute inset-0 -translate-x-full group-hover:translate-x-full transition-transform duration-1000 bg-gradient-to-r from-transparent via-white/20 to-transparent" />
      </motion.button>

      <FormationGestionDocumentaireModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        department={department}
        domain={domain}
      />
    </>
  );
}

